import type { ReactNode } from "react";
import { TeamChip } from "@/components/app/team-chip";
import { cn } from "@/lib/utils";
import type { slotContext } from "../../../shared/standings";
import { parseLocal } from "../../../shared/time";
import {
  type Division,
  type Field,
  gameSlots,
  type Pool,
  resolveSlot,
  type ScheduledGame,
  type Team,
} from "../../../shared/types";
import { clockOf } from "../../i18n/format";
import { slotTextOf } from "../../i18n/labels";

/**
 * What more than one of the board's views is built from: the section heading,
 * a side of a matchup with its chip, the small tags, and how a schedule row's
 * slot becomes a team or its placeholder's words. They lived at the bottom of
 * `BoardPage.tsx` until the Live view moved to a file of its own (2026-09-19)
 * and needed the same rows the Next and Results views draw.
 */

/**
 * One block of a view.
 *
 * EVERY title wears `.nxt`, whether it carries a fixed English word ("ON THE
 * FIELDS"), or a division or team name. That was not always true: the face had
 * no Cyrillic, so user-named headings dropped it and rendered a step off their
 * own siblings. It covers Cyrillic now: do not reintroduce the split.
 */
export function Section({
  title,
  note,
  aside,
  children,
}: {
  title: string;
  note?: string | undefined;
  /** Beside the heading from `adm`, under it on a phone, and outside its
   *  accessible name: the Live view's score-feed state. */
  aside?: ReactNode | undefined;
  children: ReactNode;
}) {
  const heading = (
    <h2 className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="nxt relative pl-2.5 text-lg leading-none">
        <span className="absolute left-0 top-0 h-full w-0.75 bg-brand skew-x-[-11.31deg]" />
        {title}
      </span>
      {note !== undefined && (
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {note}
        </span>
      )}
    </h2>
  );
  return (
    <section className="space-y-3">
      {aside === undefined ? (
        heading
      ) : (
        <div className="flex flex-col gap-1.5 adm:flex-row adm:flex-wrap adm:items-center adm:justify-between adm:gap-x-3">
          {heading}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export function TeamCode({ team, size = "sm" }: { team: Team; size?: "sm" | "md" | "lg" }) {
  return <TeamChip short={team.short} color={team.color} size={size} />;
}

/**
 * One side of a matchup as a row reads it: the code chip when the side is a
 * team, a dashed placeholder when it is still "winner of…", then the words.
 * The words WRAP rather than truncate: an undecided side is a sentence, and
 * a long name cut at the card edge is how somebody fails to find their team.
 */
export function SideLine({ side, size = "sm" }: { side: BoardSide; size?: "sm" | "md" }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {side.team !== null ? (
        <TeamCode team={side.team} size={size} />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "inline-flex shrink-0 rounded-sm border border-dashed border-muted-foreground/50",
            size === "md" ? "h-5 min-w-[3.5rem]" : "h-4 min-w-[2.75rem] adm:min-w-[3.25rem]",
          )}
        />
      )}
      <span
        className={cn(
          "min-w-0 font-medium text-pretty",
          size === "md" ? "text-base" : "text-sm",
          side.team === null && "text-muted-foreground",
        )}
      >
        {side.text}
      </span>
    </span>
  );
}

/** A small tag on a row: the division (brand), the field (muted). */
export function Tag({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "brand";
}) {
  return (
    <span
      className={cn(
        "rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
        tone === "brand" ? "border-brand/40 text-brand-ink" : "text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

// ---------- schedule rows ----------

export interface LabelCtx {
  teams: Team[];
  divisions: Division[];
  schedule: ScheduledGame[];
  pools: Pool[];
  fields: Field[];
}

/** A side of a matchup, with the TEAM behind it when there is one: the name
 *  alone cannot carry a jersey colour. */
export interface BoardSide {
  team: Team | null;
  text: string;
}

/**
 * A bracket game shows its placeholder ("W 2", "Winner Semifinal 1") until the
 * feeding result lands, so the schedule reads correctly all day.
 */
export function sideOf(
  game: ScheduledGame,
  side: "a" | "b",
  ctx: ReturnType<typeof slotContext>,
  labelCtx: LabelCtx,
): BoardSide {
  const slot = gameSlots(game)[side];
  const team = resolveSlot(slot, ctx);
  return { team, text: team?.name ?? slotTextOf(slot, labelCtx) };
}

/** Zone-less local string in, wall clock out. */
export function timeOf(iso: string): string {
  const d = parseLocal(iso);
  return d === null ? iso : clockOf(d);
}
